import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector, RootState } from '../../store';
import { fetchDashboardSummary } from '../../store/actions/DashboardActions';
import Header, { Action } from '../shared/TailwindComponents/Header';
import Badge from '../shared/TailwindComponents/Badge';
import DashboardGrid from './DashboardGrid';
import {
  PiCheckLight,
  PiSlidersHorizontalLight,
} from 'react-icons/pi';
import { FeatureTour, dashboardOrientationSteps } from '../shared/FeatureTour';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import Skeleton from '../shared/TailwindComponents/Skeleton';

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  /*
    Edit mode lives here because the toggle lives in the page header, but it is
    ONLY a flag: the arrangement is saved by the grid as it changes, so leaving
    edit mode discards nothing and there is no "save layout" to press.
  */
  const [editing, setEditing] = useState(false);

  const refresh = useCallback(() => {
    dispatch(fetchDashboardSummary() as any);
  }, [dispatch]);

  /*
    THERE IS NO HEADER "RESET LAYOUT" ANY MORE.

    It was a top-level action next to Refresh - a destructive button, one click
    away, permanently on a page nobody was customising. Resetting is part of
    customising, so it lives inside Customize (the edit toolbar's "Reset to
    default", which asks first). The header now reads: Customize · Refresh.

    `DashboardGrid` still OFFERS the bridge (`registerReset`) for any caller
    that wants the reset outside edit mode; this page simply declines it.
  */

  // The whole slice: the widget grid hands it to every widget, and each one
  // takes the field it renders. There is still one request behind the page.
  const dashboard = useAppSelector((state: RootState) => state.dashboard);
  const { loading, error, overview } = dashboard;

  // Always refresh on mount - the dashboard is a live summary of platform state
  // and must reflect any actions taken elsewhere (tasks, matters, projects, etc.)
  // since the user last viewed it. This fires on every navigation into the page.
  useEffect(() => {
    refresh();
  }, [refresh]);

  /*
    TWO ACTIONS, and the first one changes the page rather than leaving it.

    "Analytics" was a link OUT of the dashboard sitting in the same row as the
    controls FOR the dashboard, which is two different kinds of button wearing
    the same clothes. It is gone from here (the route still resolves, and the
    sidebar still carries it), leaving Customize as the primary - which is what
    the row is for - and Refresh.
  */
  const headerActions: Action[] = useMemo(() => ([
    {
      label: editing ? t('Done') : t('Customize'),
      icon: editing
        ? <PiCheckLight className="w-4 h-4" />
        : <PiSlidersHorizontalLight className="w-4 h-4" />,
      onClick: () => setEditing((current) => !current),
      variant: editing ? 'primary' : 'outline',
    },
    // Refresh removed - the dashboard already refreshes on mount (below), so a
    // manual button was redundant. `refresh` is still used by that effect.
  ] as Action[]), [t, editing]);

  const breadcrumbs = useMemo(() => [
    { name: t('Home'), href: '/home', current: true },
  ], [t]);

  const {
    isRunning: isDashboardTourRunning,
    handleJoyrideCallback: handleDashboardTourCallback,
  } = useFeatureTour({
    tourId: 'dashboard-orientation',
    steps: dashboardOrientationSteps,
    readyToShow: !loading && !!overview,
    autoStartDelay: 800,
  });

  if (loading && !overview) {
    return (
      <div className="flex flex-col h-full">
        <Header breadcrumbs={breadcrumbs} actions={headerActions} />
        <Skeleton variant="dashboard" />
      </div>
    );
  }

  if (error && !overview) {
    return (
      <div className="flex flex-col h-full">
        <Header breadcrumbs={breadcrumbs} actions={headerActions} />
        <div className="flex-1 flex flex-col items-center justify-center gap-4">
          <Badge color="red">{t('Failed to load dashboard')}</Badge>
          <p className="text-sm text-gray-500 dark:text-gray-400">{error}</p>
          <button
            className="text-sm text-info-600 dark:text-info-400 hover:underline"
            onClick={refresh}
          >
            {t('Retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-glass relative isolate flex flex-col h-full overflow-hidden">
      {/*
        Liquid-glass canvas. The `.glass` cards are genuinely translucent
        (backdrop-blur) but had a flat grey page behind them, so they only
        refracted grey and read as cheap boxes. This is a full Big-Sur-style
        pastel gradient — cool indigo/violet/cyan/mint, no gold — that the
        frosted cards refract (`.dash-glass .glass` drops their opacity so the
        colour comes through). It paints first/behind, fixed to the viewport.

        `-z-10` inside the `isolate` root: as a positioned layer with z auto it
        painted OVER the (unpositioned) page header, so Home showed no
        breadcrumb and no Customize button at all (feedback round 3).
      */}
      <div aria-hidden className="dash-canvas-bg pointer-events-none absolute inset-0 -z-10" />

      <Header breadcrumbs={breadcrumbs} actions={headerActions} />

      {/*
        The page itself says it is in edit mode, not only the toolbar inside
        it: a champagne rule under the header is visible however far the user
        has scrolled the grid.
      */}
      {editing && <div aria-hidden className="h-0.5 flex-shrink-0 bg-champagne" />}

      <div className="flex-1 overflow-auto custom-scrollbar-main">
        <DashboardGrid
          data={dashboard}
          editing={editing}
          onExitEditing={() => setEditing(false)}
          onRefresh={refresh}
        />
      </div>

      <FeatureTour
        steps={dashboardOrientationSteps}
        run={isDashboardTourRunning}
        onCallback={handleDashboardTourCallback}
        continuous
      />
    </div>
  );
};

export default HomePage;
